import { useState, useCallback } from 'react';

interface UsePointDraggingProps<T = any> {
  // Find draggable point at screen coordinates
  findPoint: (screenX: number, screenY: number) => T | null;
  // Called when point is being dragged
  onPointMove: (point: T, screenX: number, screenY: number) => void;
  // Called when drag ends
  onDragEnd: (point: T) => void;
}

interface UsePointDraggingResult<T = any> {
  draggingPoint: T | null;
  // Returns true if point was found and drag started
  handlePointMouseDown: (screenX: number, screenY: number) => boolean;
  handlePointMouseMove: (screenX: number, screenY: number) => void;
  handlePointMouseUp: () => void;
  handlePointMouseLeave: () => void;
}

export const usePointDragging = <T = any>({
  findPoint,
  onPointMove,
  onDragEnd
}: UsePointDraggingProps<T>): UsePointDraggingResult<T> => {
  const [draggingPoint, setDraggingPoint] = useState<T | null>(null);

  const handlePointMouseDown = useCallback((screenX: number, screenY: number): boolean => {
    const point = findPoint(screenX, screenY);
    if (point) {
      setDraggingPoint(point);
      return true; // Point found, start dragging
    }
    return false; // No point found
  }, [findPoint]);

  const handlePointMouseMove = useCallback((screenX: number, screenY: number) => {
    if (draggingPoint) {
      onPointMove(draggingPoint, screenX, screenY);
    }
  }, [draggingPoint, onPointMove]);

  const handlePointMouseUp = useCallback(() => {
    if (draggingPoint) {
      onDragEnd(draggingPoint);
      setDraggingPoint(null);
    }
  }, [draggingPoint, onDragEnd]);

  const handlePointMouseLeave = useCallback(() => {
    setDraggingPoint(null);
  }, []);

  return {
    draggingPoint,
    handlePointMouseDown,
    handlePointMouseMove,
    handlePointMouseUp,
    handlePointMouseLeave
  };
};
